import { useState, useMemo, useEffect } from "react";
import { Dumbbell, Utensils, ChevronDown, ChevronUp, Loader2, Lock, Clock, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChatMessage } from "@/types/evolve";
import { cn } from "@/lib/utils";
import { ExerciseGifModal } from "@/components/ExerciseGifModal";
interface MyPlansProps {
  messages: ChatMessage[];
  loading?: boolean;
  isPremium?: boolean;
  onUpgrade?: () => void;
  onStartWorkout?: (planText: string) => void;
  highlightedCard?: 'workout' | 'nutrition' | null;
}

interface Exercise {
  name: string;
  sets: string;
  reps: string;
  rest?: string;
  rpe?: string;
  isSection?: boolean;
}

interface WorkoutDay {
  title: string;
  exercises: Exercise[];
}

interface NutritionDay {
  title: string;
  meals: { name: string; foods: string; protein?: string; carbs?: string; fat?: string; calories?: string }[];
  totals?: { calories?: string; protein?: string; carbs?: string; fat?: string };
  goals?: { calories?: string; protein?: string; carbs?: string; fat?: string };
}

const EXERCISE_NAMES = [
  'squat', 'deadlift', 'bench', 'press', 'curl', 'row', 'pull-up', 'push-up',
  'lunges', 'plank', 'dips', 'fly', 'extension', 'raise', 'shrug', 'pulldown',
  'leg press', 'calf', 'crunch', 'sit-up', 'hip thrust', 'warm-up', 'cooldown'
];

const NUTRITION_KEYWORDS = [
  'meal', 'nutrition', 'diet', 'calories', 'protein', 'carbs',
  'breakfast', 'lunch', 'dinner', 'snack', 'macros',
  'eating plan', 'meal plan', 'food', 'recipe'
];

// Parse nutrition days from message text
function parseNutritionDays(text: string): NutritionDay[] {
  const days: NutritionDay[] = [];
  const lines = text.split('\n');
  
  let currentDay: NutritionDay | null = null;
  let inTable = false;
  let columnMap: { [key: string]: number } = {};
  
  for (const line of lines) {
    const trimmed = line.trim();
    
    // Check for day header (e.g., "**Day 1 - Monday**" or "### Day 1")
    const dayMatch = trimmed.match(/\*\*Day\s*(\d+)[:\s\-–]*([^*]*)\*\*/i) || 
                     trimmed.match(/###?\s*\*?\*?Day\s*(\d+)[:\s\-–]*([^*#]*)/i);
    
    if (dayMatch) {
      if (currentDay && currentDay.meals.length > 0) {
        days.push(currentDay);
      }
      const dayName = dayMatch[2]?.trim().replace(/\*+$/, '') || '';
      currentDay = {
        title: `Day ${dayMatch[1]}${dayName ? `: ${dayName}` : ''}`,
        meals: []
      };
      inTable = false;
      columnMap = {};
      continue;
    }
    
    // Check for meal table header - parse column order
    if (trimmed.startsWith('|') && /meal/i.test(trimmed) && /foods?/i.test(trimmed)) {
      inTable = true;
      const headers = trimmed.split('|').map(c => c.trim().toLowerCase().replace(/:/g, '')).filter(c => c);
      // Build a map of column name -> index
      headers.forEach((header, idx) => {
        columnMap[header] = idx;
      });
      console.log('[NutritionParse] Column map:', columnMap);
      continue;
    }
    
    // Skip table separator
    if (trimmed.startsWith('|') && trimmed.includes('---')) {
      continue;
    }
    
    // Parse table row for meals
    if (inTable && trimmed.startsWith('|') && currentDay && Object.keys(columnMap).length > 0) {
      const cells = trimmed.split('|').map(c => c.trim().replace(/\*+/g, '')).filter(c => c);
      
      if (cells.length >= 2) {
        const mealName = cells[0].trim();
        
        // Skip header rows or alignment rows
        if (mealName.toLowerCase() === 'meal' || mealName.includes('---')) continue;
        
        // Check if it's a totals/flex row
        if (mealName.toLowerCase().includes('total') || 
            mealName.toLowerCase().includes('flex') ||
            mealName.toLowerCase().includes('grand')) {
          // Get totals from the Total row (not Grand Total)
          if (mealName.toLowerCase() === 'total' || mealName === 'Total') {
            currentDay.totals = {
              protein: cells[columnMap['protein']] || undefined,
              carbs: cells[columnMap['carbs']] || undefined,
              fat: cells[columnMap['fat']] || undefined,
              calories: cells[columnMap['calories']] || undefined
            };
          }
          continue;
        }
        
        const foods = cells[columnMap['foods']] || cells[1] || '';
        
        if (mealName && foods && foods !== '-') {
          currentDay.meals.push({
            name: mealName,
            foods: foods,
            protein: cells[columnMap['protein']] || undefined,
            carbs: cells[columnMap['carbs']] || undefined,
            fat: cells[columnMap['fat']] || undefined,
            calories: cells[columnMap['calories']] || undefined
          });
        }
      }
    }
    
    // End of table
    if (inTable && !trimmed.startsWith('|') && trimmed !== '' && !trimmed.startsWith(':')) {
      inTable = false;
    }
  }
  
  // Don't forget the last day
  if (currentDay && currentDay.meals.length > 0) {
    days.push(currentDay);
  }
  
  console.log('[NutritionParse] Parsed days:', days.length, 'Total meals:', days.reduce((sum, d) => sum + d.meals.length, 0));
  
  return days;
}

// Parse workout days from message text
function parseWorkoutDays(text: string): WorkoutDay[] {
  const days: WorkoutDay[] = [];
  const lines = text.split('\n');
  
  let currentDay: WorkoutDay | null = null;
  let inTable = false;
  
  for (const line of lines) {
    const trimmed = line.trim();
    
    // Check for day header (e.g., "**Day 1: Upper Body**" or "### Day 1")
    const dayMatch = trimmed.match(/\*\*Day\s*(\d+)[:\s]*([^*]+)\*\*/i) || 
                     trimmed.match(/###?\s*\*?\*?Day\s*(\d+)[:\s]*([^*#]+)/i);
    
    if (dayMatch) {
      if (currentDay && currentDay.exercises.length > 0) {
        days.push(currentDay);
      }
      currentDay = {
        title: `Day ${dayMatch[1]}: ${dayMatch[2].trim().replace(/\*+$/, '')}`,
        exercises: []
      };
      inTable = false;
      continue;
    }
    
    // Check for table start
    if (trimmed.startsWith('|') && trimmed.toLowerCase().includes('exercise')) {
      inTable = true;
      continue;
    }
    
    // Skip table separator
    if (trimmed.startsWith('|') && trimmed.includes('---')) {
      continue;
    }
    
    // Parse table row
    if (inTable && trimmed.startsWith('|') && currentDay) {
      const cells = trimmed.split('|').map(c => c.trim()).filter(c => c);
      
      if (cells.length >= 2) {
        const exerciseName = cells[0].replace(/\*+/g, '').trim();
        
        // Check if it's a section header (like "Warm-up", "Main Lifts")
        const isSection = exerciseName.toLowerCase().includes('warm-up') ||
                         exerciseName.toLowerCase().includes('main lift') ||
                         exerciseName.toLowerCase().includes('accessor') ||
                         exerciseName.toLowerCase().includes('cooldown');
        
        if (exerciseName && !exerciseName.toLowerCase().includes('exercise')) {
          console.log('[WorkoutParse] Exercise:', exerciseName, '| cells:', cells);
          currentDay.exercises.push({
            name: exerciseName,
            sets: cells[1] || '',
            reps: cells[2] || '',
            rest: cells[3] || '',
            rpe: cells[4] || '',
            isSection
          });
        }
      }
    }
    
    // End of table
    if (inTable && !trimmed.startsWith('|') && trimmed !== '') {
      inTable = false;
    }
  }
  
  // Don't forget the last day
  if (currentDay && currentDay.exercises.length > 0) {
    days.push(currentDay);
  }
  
  return days;
}

function extractWorkoutPlan(messages: ChatMessage[]): { message: ChatMessage; days: WorkoutDay[] } | null {
  const aiMessages = messages.filter(m => m.isAI).reverse();
  
  for (const message of aiMessages) {
    const lowerText = message.text.toLowerCase();
    const hasTable = message.text.includes('|');
    const hasSetsReps = /\d+\s*x\s*\d+|\d+\s*sets/i.test(message.text);
    const hasExercises = EXERCISE_NAMES.some(exercise => lowerText.includes(exercise));
    
    if (hasTable && (hasSetsReps || hasExercises) && message.text.length > 200) {
      const days = parseWorkoutDays(message.text);
      if (days.length > 0) {
        return { message, days };
      }
    }
  }
  
  return null;
}

function extractNutritionPlan(messages: ChatMessage[]): ChatMessage | null {
  const aiMessages = messages.filter(m => m.isAI).reverse();
  
  for (const message of aiMessages) {
    const text = message.text;
    
    // Count the number of day headers (Day 1, Day 2, etc.)
    // A real nutrition plan has MULTIPLE days, not just a single meal breakdown
    const dayHeaderMatches = text.match(/\*\*Day\s*\d+/gi) || text.match(/###?\s*\*?\*?Day\s*\d+/gi);
    const dayCount = dayHeaderMatches ? dayHeaderMatches.length : 0;
    
    // CRITICAL: Must have at least 3 days to be considered a nutrition PLAN
    // Single meal breakdowns won't have multiple day headers
    if (dayCount < 3) {
      continue; // Skip this message - it's not a multi-day plan
    }
    
    // Look for our specific nutrition plan format markers (case-insensitive)
    const hasDailyTargets = /daily\s*nutrition\s*targets/i.test(text);
    const hasMealTable = /\|\s*meal\s*\|/i.test(text);
    const hasCaloriesInTable = /\|\s*calories\s*\|/i.test(text);
    const hasProteinInTable = /\|\s*protein\s*\|/i.test(text);
    const hasFoodsColumn = /\|\s*foods?\s*\|/i.test(text);
    
    // Strong match: has daily targets header WITH multiple days
    if (hasDailyTargets && dayCount >= 3) {
      console.log('[NutritionPlan] Found via daily targets marker with', dayCount, 'days');
      return message;
    }
    
    // Medium match: has meal table structure with foods, macros AND multiple days
    if (hasMealTable && hasFoodsColumn && (hasCaloriesInTable || hasProteinInTable) && dayCount >= 3) {
      console.log('[NutritionPlan] Found via meal table structure with', dayCount, 'days');
      return message;
    }
    
    // Alternative match: has multiple day headers with meal-related tables
    if (dayCount >= 3 && hasMealTable) {
      console.log('[NutritionPlan] Found via', dayCount, 'day headers + meal table');
      return message;
    }
  }
  
  return null;
}

function LockedPlanCard({ 
  title, 
  icon: Icon, 
  color,
  onUpgrade 
}: { 
  title: string; 
  icon: React.ElementType; 
  color: string;
  onUpgrade?: () => void;
}) {
  return (
    <div className="glass-card rounded-xl p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10" />
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-3">
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", color)}>
            <Icon className="w-5 h-5 text-foreground" />
          </div>
          <div className="flex-1">
            <h4 className="font-display font-bold text-sm">{title}</h4>
            <div className="flex items-center gap-1 text-xs text-primary">
              <Lock className="w-3 h-3" />
              <span>Premium Feature</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Unlock personalized {title.toLowerCase()}s with Premium.
        </p>
        <Button 
          onClick={onUpgrade}
          size="sm" 
          className="w-full evolve-gradient text-foreground font-display font-bold"
        >
          Upgrade to Premium
        </Button>
      </div>
    </div>
  );
}

function ExerciseRow({ exercise, onShowGif, isFirst }: { exercise: Exercise; onShowGif?: (name: string) => void; isFirst?: boolean }) {
  if (exercise.isSection) {
    return (
      <div className="pt-3 pb-1 first:pt-0">
        <span className="text-xs font-medium text-primary uppercase tracking-wide">
          {exercise.name}
        </span>
      </div>
    );
  }
  
  // The AI table format is: Exercise | Sets × Reps | Rest | Notes
  // But our parsing puts: sets = cells[1], reps = cells[2], rest = cells[3]
  // So: sets = "5 x 2-4", reps = "3-4m" (actually rest), rest = "Build the shelf." (actually notes)
  
  // Detect if "reps" field is actually a rest time (like "3-4m", "90s", "2m", "60s")
  const repsIsRestTime = exercise.reps && /^\d+(-\d+)?\s*(m|min|s|sec)?$/i.test(exercise.reps.trim());
  
  // Sets field should contain the actual sets × reps info
  const setsReps = exercise.sets || '';
  
  // Rest time is in the "reps" field when it looks like a time
  const restTime = repsIsRestTime ? exercise.reps : null;
  
  // Notes/tips are in the "rest" field (or in "reps" if it's not a rest time and not numeric)
  const notes = exercise.rest && 
    exercise.rest !== '-' && 
    exercise.rest.toLowerCase() !== 'rest' &&
    exercise.rest.length > 3 
      ? exercise.rest.replace(/^\*+|\*+$/g, '') // Remove markdown bold markers
      : (!repsIsRestTime && exercise.reps && exercise.reps.length > 5 ? exercise.reps : null);
  
  return (
    <div className="py-2 border-b border-border/20 last:border-0">
        <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {onShowGif && (
            <button
              id={isFirst ? "exercise-info-button" : undefined}
              onClick={() => onShowGif(exercise.name)}
              className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 hover:bg-blue-500/30 flex items-center justify-center transition-colors"
              title="View exercise demo"
            >
              <Info className="w-3.5 h-3.5 text-blue-400" />
            </button>
          )}
          <span className="text-sm text-foreground font-medium leading-tight">{exercise.name}</span>
        </div>
        {setsReps && (
          <span className="text-xs text-muted-foreground whitespace-nowrap">{setsReps.replace(/^\*+|\*+$/g, '')}</span>
        )}
      </div>
      {(restTime || notes) && (
        <div className="mt-1 space-y-0.5">
          {restTime && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span>{restTime} rest</span>
            </div>
          )}
          {notes && (
            <p className="text-xs text-muted-foreground/70 italic">
              {notes}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function WorkoutDayCard({ day, index, onShowGif, isFirstDay }: { day: WorkoutDay; index: number; onShowGif?: (name: string) => void; isFirstDay?: boolean }) {
  const [expanded, setExpanded] = useState(index === 0);
  const actualExercises = day.exercises.filter(e => !e.isSection);
  
  // Track first non-section exercise for the info button ID
  let firstExerciseFound = false;
  
  return (
    <div className="rounded-lg bg-secondary/20 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 flex items-center justify-between hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
            <span className="text-sm font-bold text-primary">{index + 1}</span>
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-foreground">{day.title}</p>
            <p className="text-xs text-muted-foreground">{actualExercises.length} exercises</p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>
      
      {expanded && (
        <div className="px-3 pb-3">
          {day.exercises.map((exercise, i) => {
            const isFirstExercise = isFirstDay && !exercise.isSection && !firstExerciseFound;
            if (isFirstExercise) firstExerciseFound = true;
            return (
              <ExerciseRow key={i} exercise={exercise} onShowGif={onShowGif} isFirst={isFirstExercise} />
            );
          })}
        </div>
      )}
    </div>
  );
}

function WorkoutPlanCard({ 
  plan,
  days,
  forceCollapse,
  forceExpand,
}: { 
  plan: ChatMessage;
  days: WorkoutDay[];
  forceCollapse?: boolean;
  forceExpand?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<string | null>(null);
  
  // Collapse when forceCollapse changes to true
  useEffect(() => {
    if (forceCollapse) {
      setExpanded(false);
    }
  }, [forceCollapse]);
  
  // Expand when forceExpand changes to true (for tour highlighting)
  useEffect(() => {
    if (forceExpand) {
      setExpanded(true);
    }
  }, [forceExpand]);
  const totalExercises = days.reduce((sum, d) => sum + d.exercises.filter(e => !e.isSection).length, 0);

  return (
    <>
      <div id="workout-plan-card" className="glass-card rounded-xl overflow-hidden">
        <button 
          onClick={() => setExpanded(!expanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/20">
              <Dumbbell className="w-5 h-5 text-foreground" />
            </div>
            <div className="text-left">
              <h4 className="font-display font-bold text-sm">Workout Plan</h4>
              <p className="text-xs text-muted-foreground">
                {days.length} days • {totalExercises} exercises
              </p>
            </div>
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </button>
        
        {expanded && (
          <div className="px-4 pb-4 space-y-2">
            {days.map((day, index) => (
              <WorkoutDayCard 
                key={index} 
                day={day} 
                index={index} 
                onShowGif={setSelectedExercise}
                isFirstDay={index === 0}
              />
            ))}
          </div>
        )}
      </div>
      
      <ExerciseGifModal
        isOpen={!!selectedExercise}
        onClose={() => setSelectedExercise(null)}
        exerciseName={selectedExercise || ''}
      />
    </>
  );
}

function NutritionDayCard({ day, index, dailyGoals }: { day: NutritionDay; index: number; dailyGoals?: { calories?: string; protein?: string; carbs?: string; fat?: string } }) {
  const [expanded, setExpanded] = useState(index === 0);
  
  // Calculate sum from all meals
  const parseNum = (val?: string) => {
    if (!val) return 0;
    const match = val.match(/[\d.]+/);
    return match ? parseFloat(match[0]) : 0;
  };
  
  const summedMacros = day.meals.reduce((acc, meal) => ({
    calories: acc.calories + parseNum(meal.calories),
    protein: acc.protein + parseNum(meal.protein),
    carbs: acc.carbs + parseNum(meal.carbs),
    fat: acc.fat + parseNum(meal.fat)
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  
  const hasMacros = summedMacros.calories > 0 || summedMacros.protein > 0;
  
  return (
    <div className="rounded-lg bg-secondary/20 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 flex items-center justify-between hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
            <span className="text-sm font-bold text-green-400">{index + 1}</span>
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-foreground">{day.title}</p>
            <p className="text-xs text-muted-foreground">{day.meals.length} meals</p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>
      
      {expanded && (
        <div className="px-3 pb-3">
          {/* Daily Goals at top */}
          {dailyGoals && (dailyGoals.calories || dailyGoals.protein) && (
            <div className="mb-3 p-2 rounded-md bg-primary/10 border border-primary/20">
              <p className="text-xs font-medium text-primary mb-1">Daily Goals</p>
              <div className="flex flex-wrap gap-3">
                {dailyGoals.calories && <span className="text-xs text-foreground">{dailyGoals.calories} cal</span>}
                {dailyGoals.protein && <span className="text-xs text-green-400">P: {dailyGoals.protein}</span>}
                {dailyGoals.carbs && <span className="text-xs text-blue-400">C: {dailyGoals.carbs}</span>}
                {dailyGoals.fat && <span className="text-xs text-yellow-400">F: {dailyGoals.fat}</span>}
              </div>
            </div>
          )}
          
          {day.meals.map((meal, i) => (
            <div key={i} className="py-2 border-b border-border/20 last:border-0">
              <div className="flex items-start justify-between gap-2">
                <span className="text-xs font-medium text-primary uppercase">{meal.name}</span>
                {meal.calories && <span className="text-xs text-muted-foreground">{meal.calories} cal</span>}
              </div>
              <p className="text-sm text-foreground/80 mt-1">{meal.foods}</p>
              {(meal.protein || meal.carbs || meal.fat) && (
                <div className="flex gap-3 mt-1">
                  {meal.protein && <span className="text-xs text-green-400">P: {meal.protein}</span>}
                  {meal.carbs && <span className="text-xs text-blue-400">C: {meal.carbs}</span>}
                  {meal.fat && <span className="text-xs text-yellow-400">F: {meal.fat}</span>}
                </div>
              )}
            </div>
          ))}
          
          {/* Calculated sum at bottom */}
          {hasMacros && (
            <div className="pt-3 mt-2 border-t border-border/30">
              <p className="text-xs font-medium text-muted-foreground mb-1">Day Total</p>
              <div className="flex flex-wrap gap-3">
                {summedMacros.calories > 0 && <span className="text-xs font-medium text-foreground">{Math.round(summedMacros.calories)} cal</span>}
                {summedMacros.protein > 0 && <span className="text-xs text-green-400">P: {Math.round(summedMacros.protein)}g</span>}
                {summedMacros.carbs > 0 && <span className="text-xs text-blue-400">C: {Math.round(summedMacros.carbs)}g</span>}
                {summedMacros.fat > 0 && <span className="text-xs text-yellow-400">F: {Math.round(summedMacros.fat)}g</span>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NutritionPlanCard({ 
  plan,
  emptyMessage,
  forceCollapse,
}: { 
  plan: ChatMessage | null;
  emptyMessage: string;
  forceCollapse?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  
  // Collapse when forceCollapse changes to true
  useEffect(() => {
    if (forceCollapse) {
      setExpanded(false);
    }
  }, [forceCollapse]);
  
  if (!plan) {
    return (
      <div id="nutrition-plan-card" className="glass-card rounded-xl p-4 opacity-60">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-500/20">
            <Utensils className="w-5 h-5 text-foreground" />
          </div>
          <div>
            <h4 className="font-display font-bold text-sm">Nutrition Plan</h4>
            <p className="text-xs text-muted-foreground">{emptyMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  // Parse the nutrition plan into structured days
  const days = parseNutritionDays(plan.text);
  const totalMeals = days.reduce((sum, d) => sum + d.meals.length, 0);
  
  // Extract daily goals from the plan text - look for Daily Nutrition Targets table
  const extractGoals = () => {
    const text = plan.text;
    const goals: { calories?: string; protein?: string; carbs?: string; fat?: string } = {};
    
    // Parse the Daily Nutrition Targets table format:
    // | Calories | 2,300 kcal | Forged for fat loss. |
    // | Protein | 200g | Your armor... |
    const lines = text.split('\n');
    for (const line of lines) {
      if (line.includes('|')) {
        const cells = line.split('|').map(c => c.trim());
        const cellsLower = cells.map(c => c.toLowerCase());
        
        // Look for Calories row
        if (cellsLower.some(c => c === 'calories')) {
          for (const cell of cells) {
            const match = cell.match(/(\d[\d,]*)\s*(?:kcal|cal)/i);
            if (match) {
              goals.calories = match[1].replace(/,/g, '');
              break;
            }
          }
        }
        
        // Look for Protein row
        if (cellsLower.some(c => c === 'protein')) {
          for (const cell of cells) {
            const match = cell.match(/(\d+)\s*g/i);
            if (match) {
              goals.protein = match[1] + 'g';
              break;
            }
          }
        }
        
        // Look for Carbs row
        if (cellsLower.some(c => c.includes('carb'))) {
          for (const cell of cells) {
            const match = cell.match(/(\d+)\s*g/i);
            if (match) {
              goals.carbs = match[1] + 'g';
              break;
            }
          }
        }
        
        // Look for Fat row (but not "Fat Loss" or similar)
        if (cellsLower.some(c => c === 'fat')) {
          for (const cell of cells) {
            const match = cell.match(/(\d+)\s*g/i);
            if (match) {
              goals.fat = match[1] + 'g';
              break;
            }
          }
        }
      }
    }
    
    // Fallback: Look for inline patterns
    if (!goals.calories) {
      const calMatch = text.match(/(?:daily|target|goal)[:\s]*(\d[\d,]*)\s*(?:cal|kcal)/i) ||
                       text.match(/(\d[\d,]{2,})\s*(?:cal|kcal)\s*(?:target|daily|goal|\/day)/i);
      if (calMatch) goals.calories = calMatch[1].replace(/,/g, '');
    }
    if (!goals.protein) {
      const proteinMatch = text.match(/protein[:\s|]*(\d+)\s*g/i);
      if (proteinMatch) goals.protein = proteinMatch[1] + 'g';
    }
    if (!goals.carbs) {
      const carbsMatch = text.match(/carb(?:s|ohydrate)?[:\s|]*(\d+)\s*g/i);
      if (carbsMatch) goals.carbs = carbsMatch[1] + 'g';
    }
    if (!goals.fat) {
      const fatMatch = text.match(/\bfat[:\s|]*(\d+)\s*g/i);
      if (fatMatch) goals.fat = fatMatch[1] + 'g';
    }
    
    console.log('[NutritionGoals] Extracted:', goals);
    return Object.keys(goals).length > 0 ? goals : undefined;
  };
  
  const dailyGoals = extractGoals();
  
  return (
    <div id="nutrition-plan-card" className="glass-card rounded-xl overflow-hidden">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-500/20">
            <Utensils className="w-5 h-5 text-foreground" />
          </div>
          <div className="text-left">
            <h4 className="font-display font-bold text-sm">Nutrition Plan</h4>
            <p className="text-xs text-muted-foreground">
              {days.length > 0 
                ? `${days.length} days • ${totalMeals} meals`
                : dailyGoals?.calories 
                  ? `${dailyGoals.calories} cal target`
                  : 'Tap to view plan'
              }
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </button>
      
      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          {days.length > 0 ? (
            days.map((day, index) => (
              <NutritionDayCard key={index} day={day} index={index} dailyGoals={dailyGoals} />
            ))
          ) : (
            <div className="text-sm text-foreground/80 whitespace-pre-wrap">
              {plan.text.slice(0, 1500)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MyPlans({ messages, loading, isPremium = false, onUpgrade, highlightedCard }: MyPlansProps) {
  const workoutPlan = useMemo(() => extractWorkoutPlan(messages), [messages]);
  const nutritionPlan = useMemo(() => extractNutritionPlan(messages), [messages]);

  console.log('[MyPlans] isPremium:', isPremium, 'nutritionPlan found:', !!nutritionPlan, 'messages count:', messages.length);
  
  const hasAnyPlan = workoutPlan || nutritionPlan;

  return (
    <div className="space-y-3">
      <h3 className="font-display font-bold text-lg">My Plans</h3>

      {loading && !hasAnyPlan ? (
        <div className="glass-card rounded-xl p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-3">
          {workoutPlan ? (
            <WorkoutPlanCard
              plan={workoutPlan.message}
              days={workoutPlan.days}
              forceCollapse={highlightedCard === 'nutrition'}
              forceExpand={highlightedCard === 'workout'}
            />
          ) : (
            <div id="workout-plan-card" className="glass-card rounded-xl p-4 opacity-60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/20">
                  <Dumbbell className="w-5 h-5 text-foreground" />
                </div>
                <div>
                  <h4 className="font-display font-bold text-sm">Workout Plan</h4>
                  <p className="text-xs text-muted-foreground">Ask your coach for a workout plan</p>
                </div>
              </div>
            </div>
          )}
          {isPremium ? (
            <NutritionPlanCard
              plan={nutritionPlan}
              emptyMessage="Ask your coach for a nutrition plan"
              forceCollapse={highlightedCard === 'workout'}
            />
          ) : (
            <LockedPlanCard
              title="Nutrition Plan"
              icon={Utensils}
              color="bg-green-500/20"
              onUpgrade={onUpgrade}
            />
          )}
        </div>
      )}

      {!hasAnyPlan && !loading && (
        <p className="text-xs text-center text-muted-foreground mt-2">
          Try saying "Give me a full 4-day workout plan" in chat
        </p>
      )}
    </div>
  );
}